import { Button } from 'components/core';
import { usePhoneValidation } from 'hooks/usePhoneValidation';
import { useTitleCase } from 'hooks/useTitleCase';
import { maskTaxId } from 'helpers/modules/helperVendor';
import { StatusTag } from 'components/core/Chips/StatusTag/StatusTag';
import { CellContent } from '../CellElements/CellContent/CellContent';
import Tooltip from 'components/core/Tooltip/Tooltip';
import { NotOnboardedColumnWidth } from './ColumnWidths';

export interface InviteActionCellProps {
  row: {
    inviteStatus?: 'invited' | 'not_invited';
    invitedAt?: string | null;
    canInvite?: boolean;
    identityKey?: string;
  };
  onInvite: (identityKey: string) => void;
  isAdmin: boolean;
}

export const InviteActionCell = ({ row, onInvite, isAdmin }: InviteActionCellProps) => {
  // Already-invited rows reuse the same button, relabeled "Re-invite" (backend
  // re-invite is idempotent + re-sends the email). No-contact rows stay disabled.
  const isInvited = row.inviteStatus === 'invited';
  const disabled = !isAdmin || row.canInvite === false;
  const invitedWhen = row.invitedAt ? new Date(row.invitedAt).toLocaleDateString() : '';

  return (
    <div className="inline-flex items-center group relative">
      <Button
        buttonLabel={isInvited ? 'Re-invite' : 'Invite'}
        isDisabled={disabled}
        variants="extraSmall"
        onClick={() => row.identityKey && onInvite(row.identityKey)}
      />
      {row.canInvite === false && (
        <Tooltip
          tooltipIcon="Info"
          additionalClasses="ml-2"
          tooltipIconClasses="text-grey-600 cursor-pointer"
          tooltipContent="No contact info on file"
          tooltipContaineClasses="right-0 -top-4 rounded-tr-lg pointer-events-none"
        />
      )}
      {isInvited && row.canInvite !== false && (
        <Tooltip
          tooltipIcon="Info"
          additionalClasses="ml-2"
          tooltipIconClasses="text-grey-600 cursor-pointer"
          tooltipContent={`Invited ${invitedWhen}`}
          // Last column: open leftward so the popover doesn't spill past the
          // table's right edge and get clipped.
          tooltipContaineClasses="right-0 -top-4 rounded-tr-lg pointer-events-none"
        />
      )}
    </div>
  );
};

/**
 * Format a phone number consistently with the onboarded vendor columns.
 */
const formatPhone = (phone?: string | null) => {
  if (!phone) {
    return null;
  }

  const getPhoneNumber = String(phone).replace(/\D/g, '');
  const getRestOfTheFirstNumber =
    getPhoneNumber.substring(0, 1) === '1' ? getPhoneNumber.slice(1) : getPhoneNumber;

  return usePhoneValidation(getRestOfTheFirstNumber);
};

/**
 * Column definitions for the Not-onboarded vendors tab.
 * Anchored on CoupaSupplier fields (no Account row-link / net-term editing).
 */
export const handleNotOnboardedTableColumn = (
  onInvite?: (identityKey: string) => void,
  isAdmin?: boolean,
  selectedKeys?: string[],
  onSelectKey?: (identityKey: string, checked: boolean) => void,
  onCompleteOnboarding?: (vendor: any) => void,
  canOnboard?: boolean
) => {
  const selectionColumn = isAdmin
    ? [
        {
          Header: () => null,
          id: 'bulk-select',
          accessor: () => 'bulk-select',
          disableSortBy: true,
          canFilter: false,
          wrapperClasses: 'w-8 min-w-[32px]',
          headerClasses: 'w-8 min-w-[32px]',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          Cell: (row) => {
            // eslint-disable-next-line react/destructuring-assignment
            const { identityKey, canInvite } = row.cell.row.original;
            // Invited rows stay selectable (bulk re-invite is fine — it's idempotent).
            // Only no-contact vendors are non-selectable, since they can't be invited.
            if (!identityKey || canInvite === false) return null;
            const checked = selectedKeys?.includes(identityKey) ?? false;
            return (
              <input
                type="checkbox"
                aria-label={`Select ${identityKey}`}
                checked={checked}
                onChange={(e) => onSelectKey && onSelectKey(identityKey, e.target.checked)}
                className="cursor-pointer"
              />
            );
          },
        },
      ]
    : [];

  const notOnboardedTableColumns: any[] = [
    ...selectionColumn,
    {
      Header: 'VENDOR',
      accessor: (row: any) => row?.name ?? '',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.name,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.name,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { name } = row.cell.row.original;
        const displayName = name ? useTitleCase(name) : null;

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.name}
            additionalClasses="capitalize"
          >
            {displayName || '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'CODE',
      accessor: 'code',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.code,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.code,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { code } = row.cell.row.original;

        return (
          <CellContent wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.code}>
            {code ? <span className="!normal-case !text-inherit">{code}</span> : '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'EMAIL',
      accessor: 'email',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.email,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.email,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { email } = row.cell.row.original;

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.email}
            additionalClasses="normal-case"
            isLinkWrapped
          >
            {email || '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'CONTACT',
      accessor: (row: any) => row?.primaryContactName ?? '',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.contact,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.contact,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { primaryContactName, phone } = row.cell.row.original;
        const contactName = primaryContactName ? useTitleCase(primaryContactName) : null;
        const formattedPhone = formatPhone(phone);

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.contact}
            additionalClasses="capitalize"
          >
            {contactName || formattedPhone ? (
              <span className="flex flex-col">
                {contactName && <span>{contactName}</span>}
                {formattedPhone && (
                  <span className="normal-case text-grey-600">{formattedPhone}</span>
                )}
              </span>
            ) : (
              '—'
            )}
          </CellContent>
        );
      },
    },
    {
      Header: 'LOCATION',
      accessor: (row: any) => [row?.city, row?.state].filter(Boolean).join(', '),
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.location,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.location,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { city, state } = row.cell.row.original;
        const location = [city, state].filter(Boolean).join(', ');

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.location}
            additionalClasses="capitalize"
          >
            {location || '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'TAX ID',
      accessor: (row: any) => row?.taxId ?? '',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.tax_id,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.tax_id,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { taxId } = row.cell.row.original;
        const masked = maskTaxId(taxId);

        return (
          <CellContent wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.tax_id}>
            {masked || '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      disableSortBy: true,
      canFilter: false,
      gridType: 'vendors',
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.status,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.status,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const { status } = row.cell.row.original;

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.status}
            isRemoveCellZindex
          >
            {status ? <StatusTag status={status} /> : '—'}
          </CellContent>
        );
      },
    },
    {
      Header: 'ACTION',
      accessor: () => 'action',
      disableSortBy: true,
      canFilter: false,
      wrapperClasses: NotOnboardedColumnWidth.ColomnWidth.action,
      headerClasses: NotOnboardedColumnWidth.HeaderCellWidth.action,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment
        const original = row.cell.row.original;

        return (
          <CellContent
            wrapperClasses={NotOnboardedColumnWidth.ColomnWidth.action}
            isRemoveCellZindex
          >
            <div className="inline-flex items-center gap-2">
              <InviteActionCell
                row={original}
                onInvite={onInvite ?? (() => {})}
                isAdmin={isAdmin ?? false}
              />
              {canOnboard && (
                <Button
                  buttonLabel="Onboard"
                  variants="extraSmall"
                  onClick={() => onCompleteOnboarding && onCompleteOnboarding(original)}
                />
              )}
            </div>
          </CellContent>
        );
      },
    },
  ];

  return notOnboardedTableColumns;
};
